import { FormInstance, message } from 'antd'
import { PropertyType, PtoContractArgs, RentalInfoPeriod } from 'Modules'
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAccount } from 'wagmi'

import { AppAdmin } from 'api/app-admin'
import { PTOTypeEnum, TermsFlowEnum, TokenizeStatusEnum } from 'constants/pto'
import {
  cleanCoTrustedPartners,
  cleanGoldenTickets,
  cleanMetadata,
  cleanPhases,
  INITIAL_CO_TRUSTED_PARTNER,
  INITIAL_GOLDEN_TICKET,
  initTimeLinesCreate,
  toRentalPeriodPayload,
} from 'extends/pto.extend'
import { customErrorContract } from 'hooks/contract/use-contract-viem'
import { PAGES } from 'routes'
import { onCloseModalProcessing, onOpenModalProcessing } from 'stores/modal.store'
import { logError, logInfo } from 'utils/logger'

import { useDeployContract } from './use-deploy-contract'

interface UseCreatePTOProps {
  form: FormInstance
}

const useCreatePTO = ({ form }: UseCreatePTOProps) => {
  const navigate = useNavigate()
  const [thumbnail, setThumbnail] = useState<string>('')
  const [thumbnails, setThumbnails] = useState<string[]>([])
  const [propertyTypes, setPropertyTypes] = useState<PropertyType[]>([])
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { address } = useAccount()
  const { isContractManager, deployContract } = useDeployContract()

  const handleGetData = useCallback(async () => {
    try {
      const resListPropertyType = await AppAdmin.getListProperty()
      const types: PropertyType[] = []
      if (resListPropertyType?.data.success) {
        resListPropertyType?.data.data.map((item: PropertyType) => {
          types.push({
            ...item,
            id: Number(item.id),
          })
        })
        setPropertyTypes(types)
      }
      const formValues = {
        metadata: {},
        phases: initTimeLinesCreate(),
        goldenTickets: [INITIAL_GOLDEN_TICKET],
        coTrustedPartners: [INITIAL_CO_TRUSTED_PARTNER],
      }
      form.setFieldsValue(formValues)
    } catch (error: any) {
      logError('Upload error:', error)
    }
  }, [form])

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true)
      const values = await form.validateFields()
      const {
        type,
        ptoType,
        name,
        country,
        description = '',
        isFeatured,
        isShowDetail,
        isShowList,
        latitude,
        longitude,
        propertyItems,
        metadata: formMetadata,
        phases: formPhases,
        maxNFTSupply,
        pricePerNFT,
        royalty,
        tradeUnlockTime,
        transactionFee,
        ownerAddress,
        goldenTickets: formGoldenTickets,
        typeSocial,
        rentalPeriods,
        isLeasingInProgress,
        terms,
        coTrustedPartners: formCoTrustedPartners,
      } = values

      const metadata = cleanMetadata(formMetadata)
      const phases = cleanPhases(formPhases)
      const goldenTickets = cleanGoldenTickets(formGoldenTickets)
      const coTrustedPartners = cleanCoTrustedPartners(formCoTrustedPartners)

      const params = {
        fullName: name,
        country: country,
        description: description,
        isFeatured: !!isFeatured,
        isShowDetail: !!isShowDetail,
        isShowList: !!isShowList,
        type: type,
        ptoType: ptoType,
        latitude: latitude,
        longitude: longitude,
        propertyItems: propertyItems,
        metadata: metadata,
        thumbnail: thumbnail || '',
        thumbnails: thumbnails || [],
        ownerAddress: ownerAddress,
        tradeUnlockTime: tradeUnlockTime || 0,
        maxNFTSupply,
        pricePerNFT,
        royaltyFee: royalty,
        buyingFeeBP: transactionFee,
        phases: phases,
        goldenTickets,
        typeSocial: typeSocial || {},
        isLeasingInProgress: !!isLeasingInProgress,
        terms: {
          content: terms || '',
          flowType: TermsFlowEnum.PURCHASE,
        },
        coTrustedPartners,
      }
      onOpenModalProcessing()
      logInfo('Submitting PTO with params:', params)

      const results = await AppAdmin.ptoCreate(params)
      if (results.status === 200) {
        const creatPto = results.data.data as PtoContractArgs

        if (ptoType === PTOTypeEnum.RENTAL_PTO && rentalPeriods?.length) {
          for (const period of rentalPeriods as RentalInfoPeriod[]) {
            await AppAdmin.createRentalInfo(creatPto.externalId, toRentalPeriodPayload(period))
          }
        }

        if (address) {
          if (isContractManager) {
            const contractAddress = await deployContract(address, creatPto)
            if (contractAddress) {
              await AppAdmin.ptoUpdateContract(creatPto.externalId, {
                contractAddress,
                status: TokenizeStatusEnum.ACTIVE,
              })
              onCloseModalProcessing()
              message.success('PTO created successfully!')
            }
          } else {
            onCloseModalProcessing()
            message.error('Your wallet address does not have permission to create PTO!')
          }
        } else {
          onCloseModalProcessing()
          message.error('Please connect wallet!')
        }
        navigate(PAGES.PTO)
      }
    } catch (error: any) {
      onCloseModalProcessing()
      const errorMessage = customErrorContract(error)
      message.error(errorMessage || 'Error while submitting form')
      logError('Error while submitting form:', errorMessage)
    } finally {
      onCloseModalProcessing()
      setIsSubmitting(false)
    }
  }

  const handleCancel = () => {
    navigate(PAGES.PTO)
  }

  useEffect(() => {
    handleGetData()
  }, [handleGetData])

  return {
    thumbnail,
    setThumbnail,
    thumbnails,
    setThumbnails,
    propertyTypes,
    isSubmitting,
    handleSubmit,
    handleCancel,
  }
}

export default useCreatePTO
